import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SubmissionDetailComponent } from '../submission-detail/submission-detail.component';
import { MaskitoOptions, maskitoTransform } from '@maskito/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-sin-submission-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sin-submission-detail.component.html',
  styleUrls: ['./sin-submission-detail.component.scss', '../submission-detail/submission-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SinSubmissionDetailComponent extends SubmissionDetailComponent implements OnChanges {
  @Input() value: string = '';

  public disabled: boolean = false;

  private _value = new BehaviorSubject<string>('');
  public value$ = this._value as Observable<string>;

  private _showingSin = new BehaviorSubject<boolean>(false);
  public showingSin$: Observable<boolean> = this._showingSin.asObservable();

  public showMaskedSin$: Observable<boolean> = combineLatest([this.showingSin$, this.value$]).pipe(
    map(([showing, value]) => !showing && !!value)
  );

  public displayedValue$: Observable<string> = combineLatest([this.value$, this.showingSin$]).pipe(
    map(([value, showing]) => (showing ? this.formatVisible(value) : this.getHiddenSINString(value, showing)))
  );

  public maskOptions: MaskitoOptions = {
    mask: [/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/]
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this._value.next(this.value);
    }
  }

  public toggleShowHide(): void {
    if (this.disabled) return;
    this.showingSin$.pipe(take(1)).subscribe(current => {
      this._showingSin.next(!current);
    });
  }

  private formatVisible(input: string): string {
    return maskitoTransform(this.getUnmaskedValue(input), this.maskOptions);
  }

  private getUnmaskedValue(value: string): string {
    return value.replace(/\D/g, '');
  }

  private getHiddenSINString(value: string, showing: boolean): string {
    const raw = this.getUnmaskedValue(value);
    if (!raw) return '';
    const chars: string[] = [];
    for (let i = 0; i < raw.length; i++) {
      if (i > 0 && i % 3 === 0) chars.push(' ');
      if (showing) {
        chars.push(raw[i]);
      } else {
        if (raw.length >= 7 && i >= 6) {
          chars.push(raw[i]);
        } else {
          chars.push(`<span class="masked-character">\u23FA</span>`);
        }
      }
    }
    return chars.join('');
  }
}
